import { FC, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router';
import { Row, Typography } from 'antd';

import { getPrimaryMarketItem } from 'features/primaryMarket/actions';

import { Loading, RejectedMessage, AgreeWithTerms } from 'componets/ui';
import { PrimaryMarketItemPhotos } from './PrimaryMarketItemPhotos';
import { PrimaryItemPaymentSchedule } from './PrimaryMarketProjectPaymentSchedule';
import { PrimaryMarketProjectParams } from './PrimaryMarketProjectParams';
import { PrimaryMarketProjectNews } from './PrimaryMarketProjectNews';
import { ProjectTitle } from './ProjectTitle';
import { PrimaryMarketProjectDocuments } from './PrimaryMarketProjectDocuments';
import { PrimaryMarketProjectInvest } from './PrimaryMarketProjectInvest';

import { useAppSelector, useTranslate } from 'hooks';

import { RequestStatus } from 'utils/constants';
import { language } from 'utils/i18n';
import { AppDispatch } from 'store';
import { selectUser } from 'selectors';
import { ProjectNews } from 'models';

import { ReactComponent as ArrowIcon } from 'assets/icons/arrow-select.svg';

import './index.scss';
import i18next from "i18next";
import {PrimaryMarketProcessedPayments} from "./PrimaryMarketProcessedPayments";
import { ScrollToTopButton } from 'componets/common';

const WORDS = [
	'projectDescriptionText',
	'collateralInformationText',
	'baseRateText',
	'readNewsText',
];

export const PrimaryMarketProject: FC = () => {
	const { collateralInformationText, projectDescriptionText, baseRateText, readNewsText } =
		useTranslate(WORDS);

	const { item: project, status } = useAppSelector((state) => state.primaryMarket);
	const user = useAppSelector(selectUser);
	const [news, setNews] = useState<ProjectNews>({} as ProjectNews);

	const dispatch = useDispatch<AppDispatch>();

	const { projectId } = useParams();

	useEffect(() => {
		if (projectId) {
			dispatch(getPrimaryMarketItem(projectId));
		}
	}, [dispatch, projectId]);

	const { collateral, projectDescription } = useMemo(() => {
		let collateral: string | null = null;
		let projectDescription: string | null = null;

		project.description?.forEach((item) => {
			if (item[language]?.key === 'collateral') {
				collateral = item[language]?.body;
			} else if (item[language]?.key === 'default') {
				projectDescription = item[language]?.body;
			}

			if (collateral && projectDescription) {
				return;
			}
		});

		return { collateral, projectDescription };
	}, [project.description]);

	const documents = useMemo(
		() => project.documents?.filter((item) => item.lang === language),
		[project.documents],
	);

	const isRejected = status === RequestStatus.REJECTED;
	const projectNewsRef = useRef<HTMLDivElement>(null);
	const scrollToProjectNews = () => {
		projectNewsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
	};

	return (
		<>
			{!user.agreeWithTerms && <AgreeWithTerms />}

			{isRejected ? (
				<RejectedMessage isNotFound />
			) : !project.country || (projectId && +projectId !== project?.loanId) ? (
				<Loading />
			) : (
				<div className="primaryItem">
					<div className="primaryItem__mobileTitle">
						<ProjectTitle />
					</div>

					<Row className="primaryItem__photos">
						{project.photos?.length > 0 && <PrimaryMarketItemPhotos images={project.photos} />}

						<div className="primaryItem__column">
							<div className="primaryItem__card">
								<PrimaryMarketProjectInvest />
							</div>

							{news.items?.length > 0 && (
								<button
									aria-label="Go to project news"
									className="primaryItem__projectNews"
									onClick={scrollToProjectNews}
								>
									<span>{readNewsText}</span>
									<ArrowIcon />
								</button>
							)}
						</div>
					</Row>

					<div className="primaryItem__info">
						<PrimaryMarketProjectParams />

						<div className="primaryItem__block">
							<Typography.Title className="primaryItem__blockTitle" level={4}>
								{baseRateText}
							</Typography.Title>
							<Typography.Text>{project.interest} %</Typography.Text>
						</div>

						{projectDescription && (
							<div className="primaryItem__block">
								<Typography.Title className="primaryItem__blockTitle" level={4}>
									{projectDescriptionText}
								</Typography.Title>
								<div
									className="primaryItem__projectList"
									dangerouslySetInnerHTML={{ __html: projectDescription }}
								/>
							</div>
						)}

						{collateral && (
							<div className="primaryItem__block">
								<Typography.Title className="primaryItem__blockTitle" level={4}>
									{collateralInformationText}
								</Typography.Title>
								<div
									className="primaryItem__projectList"
									dangerouslySetInnerHTML={{ __html: collateral }}
								/>
							</div>
						)}

						{documents?.length > 0 && <PrimaryMarketProjectDocuments documents={documents} />}
					</div>

					<PrimaryItemPaymentSchedule
						data={project.paymentSchedule}
						archive={project.scheduleArchive}
						notes={project.params.filter(
							(el) => el.pledgeKey === 'note' && el.language === i18next.language,
						)}
					/>

					{project?.processedPayments.length > 0 && (
						<PrimaryMarketProcessedPayments data={project.processedPayments} />
					)}

					<div ref={projectNewsRef}>
						<PrimaryMarketProjectNews news={news} setNews={setNews} />
					</div>

					<div className="primaryItem__scrollToTop">
						<ScrollToTopButton />
					</div>
				</div>
			)}
		</>
	);
};
